import {
  Box,
  Button,
  Code,
  HStack,
  IconButton,
  Input,
  Select,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { SectionProps } from "../../sections/sectionsComponents";
import {
  EventDetailPixelsSection,
  KYDPixelSettings,
} from "@common/interfaces/KYDEvent";
import PixelsAddEditDialog, {
  PixelsAddEditDialogContext,
  withPixelsAddEditDialog,
} from "./PixelsAddEditDialog";
import { useCallback, useState } from "react";
import ShowIfHasPermission from "../../../ShowIfHasPermission";
import {
  FaInstagram,
  FaPlus,
  FaSnapchatGhost,
  FaTiktok,
  FaTrash,
  FaMailBulk,
  FaGoogle,
} from "react-icons/fa";
import { useDialog, useDialogWithOptions } from "../../../../Common/Dialog";
import actions from "../../../../actions";
import { useLoading } from "../../../hooks/form";
import { FiEdit2 } from "react-icons/fi";
import { AddIcon, CheckCircleIcon, MinusIcon } from "@chakra-ui/icons";
import { useFieldArray, useForm } from "react-hook-form";
import { KYDMarketingPlatform } from "@common/interfaces/KYDOrganization";

const types = [
  {
    value: "facebook",
    text: "Meta",
  },
  {
    value: "tiktok",
    text: "TikTok",
  },
  {
    value: "snapchat",
    text: "Snapchat",
  },
  {
    value: "hive",
    text: "Hive",
  },
  {
    value: "ga",
    text: "Google Tag",
  },
];

const platformIcon = (platform: KYDMarketingPlatform) => {
  //@ts-ignore
  if (platform === "meta" || platform === "facebook") {
    return <FaInstagram size={"20px"} />;
  } else if (platform === "snapchat") {
    return <FaSnapchatGhost size={"20px"} />;
  } else if (platform === "tiktok") {
    return <FaTiktok size={"20px"} />;
  } else if (platform === "hive") {
    return <FaMailBulk size={"20px"} />;
  } else if (platform === "ga") {
    return <FaGoogle size={"20px"} />;
  }
};

const PixelsSection = ({
  data,
  isLoading,
  permissions,
  refetchEvent,
}: SectionProps<EventDetailPixelsSection["data"]>) => {
  const [loading, setLoading] = useLoading({
    saving: false,
  });
  const dialog = useDialog();
  const [isEditing, setIsEditing] = useState(false);
  const { open: openPixelsAddEditDialog, close: closePixelsAddEditDialog } =
    useDialogWithOptions(PixelsAddEditDialogContext);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isValid, errors },
  } = useForm({
    mode: "onChange",
    criteriaMode: "all",
    reValidateMode: "onChange",
    values: {
      pixels: data.marketing_pixels || [],
    },
  });

  const readOnly = !permissions.includes("write");

  const onSavePixel = useCallback(
    async (pixelData: { pixels: KYDPixelSettings[] }) => {
      console.log({
        pixelData,
      });

      try {
        setLoading("saving", true);
        console.log(pixelData);
        await actions.ADMIN.updateEventPixels({
          event_id: data.id,
          pixels: pixelData.pixels,
        });

        await refetchEvent({
          silent: true,
        });
        closePixelsAddEditDialog();
        setIsEditing(false);
      } catch (e) {
        dialog({
          title: "Error",
          text: e.message,
        });
      }
      setLoading("saving", false);
    },
    [data]
  );

  const {
    append,
    fields: _fields,
    remove,
  } = useFieldArray({
    control,
    name: "pixels",
    keyName: "field_id",
  });

  const controlledPixels = watch("pixels");
  const controlledFields = _fields.map((field, idx) => {
    return {
      ...field,
      ...controlledPixels[idx],
    };
  });

  return (
    <Stack>
      <Stack
        direction={"row"}
        justify={"space-between"}
        w="100%"
        alignItems={"center"}
      >
        <Text fontWeight={"bold"}>Pixels</Text>
        <HStack>
          <ShowIfHasPermission permissions={permissions} checkFor="write">
            <HStack>
              <IconButton
                isDisabled={isLoading || isEditing}
                variant={"outline"}
                aria-label="Edit pixels"
                icon={<FiEdit2 />}
                onClick={() => {
                  setIsEditing(true);
                }}
              />
              <IconButton
                isDisabled={isLoading}
                variant={"outline"}
                aria-label="Add pixel"
                icon={<FaPlus />}
                onClick={() => {
                  setIsEditing(true);
                  append(
                    {
                      id: "",
                      type: "facebook",
                      access_token: "",
                      ad_account_id: "",
                    },
                    {
                      shouldFocus: true,
                    }
                  );
                }}
              />
            </HStack>
          </ShowIfHasPermission>
        </HStack>
      </Stack>
      <Box overflowX={"scroll"}>
        <Table
          display="table"
          w="full"
          sx={{
            mt: "16px",
            opacity: isLoading || loading.saving ? 0.5 : 1,
            borderCollapse: "collapse",
          }}
        >
          <Thead>
            <Th minW="100px">Platform</Th>
            <Th minW={"250px"}>ID</Th>
            <Th textAlign={"center"} minW={"20%"}>
              Conversion API
            </Th>
            <Th width={"40px"}></Th>
          </Thead>
          <Tbody>
            {data?.organization_marketing_pixels.map((p) => (
              <Tr key={p.id}>
                <Td minW={"100px"}>
                  <HStack>
                    {
                      //@ts-ignore
                      platformIcon(p.type)
                    }
                    <Text fontWeight={"bold"}>
                      {types.find((t) => t.value === p.type)?.text}
                    </Text>
                  </HStack>
                </Td>
                <Td minW={"250px"}>
                  <Code>{p.id}</Code>
                </Td>
                <Td textAlign={"center"} minW={"20%"}>
                  {p.access_token ? (
                    <CheckCircleIcon color="green" />
                  ) : (
                    <MinusIcon color="lightgray" />
                  )}
                </Td>
                <Td textAlign={"right"} minW={"10%"}></Td>
              </Tr>
            ))}
            {controlledFields.map((p, index) => (
              <Tr key={p.field_id}>
                <Td minW={"200px"}>
                  {isEditing ? (
                    <Select
                      {...register(`pixels.${index}.type`)}
                      value={p.type}
                      isDisabled={!isEditing || loading.saving}
                    >
                      {types.map((pixelType) => (
                        <option key={pixelType.value} value={pixelType.value}>
                          {pixelType.text}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <HStack>
                      {
                        //@ts-ignore
                        platformIcon(p.type)
                      }
                      <Text fontWeight={"bold"}>
                        {types.find((t) => t.value === p.type)?.text}
                      </Text>
                    </HStack>
                  )}
                </Td>
                <Td minW={"250px"}>
                  {isEditing ? (
                    <Input
                      {...register(`pixels.${index}.id`, {
                        required: true,
                      })}
                      isDisabled={loading.saving}
                      placeholder="Pixel ID"
                      type="text"
                    />
                  ) : (
                    <Code>{p.id}</Code>
                  )}
                </Td>
                <Td textAlign={"center"} minW={"20%"}>
                  {p.access_token ? (
                    <CheckCircleIcon color="green" />
                  ) : (
                    <MinusIcon color="lightgray" />
                  )}
                </Td>
                <Td textAlign={"right"} minW={"10%"}>
                  {isEditing ? (
                    <IconButton
                      isDisabled={isLoading || loading.saving}
                      variant={"ghost"}
                      aria-label="Remove "
                      icon={<FaTrash />}
                      onClick={() => {
                        remove(index);
                      }}
                    />
                  ) : null}
                </Td>
              </Tr>
            ))}
            <Tr>
              <Td colSpan={4}>
                <Button
                  leftIcon={<AddIcon />}
                  isDisabled={loading.saving}
                  size="sm"
                  onClick={() => {
                    setIsEditing(true);
                    append({
                      id: "",
                      type: "facebook",
                      access_token: "",
                      ad_account_id: "",
                    });
                  }}
                >
                  Add a new pixel
                </Button>
                {isEditing ? (
                  <HStack mt={8}>
                    <Button
                      isLoading={loading.saving}
                      isDisabled={loading.saving || !isValid}
                      disabled={!isValid}
                      colorScheme={"green"}
                      onClick={handleSubmit(onSavePixel)}
                    >
                      Save
                    </Button>
                    <Button
                      isDisabled={loading.saving}
                      isLoading={loading.saving}
                      colorScheme={"red"}
                      onClick={() => {
                        reset();
                        setIsEditing(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </HStack>
                ) : null}
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
    </Stack>
  );
};

export default withPixelsAddEditDialog(PixelsSection);
